import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './router.js';
import './index.css';

/**
 * One build serves three shells — employee, company admin and platform console
 * (SRS §2, §21). Which shell renders is decided by the route and the session's
 * roles, not by three separate bundles, so a user who is both an employee and
 * an HR admin does not load the app twice.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry an auth or authorization failure: the client already
        // tried a token refresh, so a retry would only burn rate limit.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 422) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
